/**
 * Player-stats modal. The ONLY modal in the MVP — everything else is a
 * route. Opens via setSelectedPlayerId in GameView; closes on:
 *   - ESC key
 *   - click on the backdrop (outside the dialog)
 *   - the explicit close button (≥44px tap target)
 *
 * Focus management: on open, focus jumps to the close button; on close, we
 * restore focus to the element that triggered the open (the lineup row,
 * fielder, or pitcher chip). Tab cycles within the dialog.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePlayerStats } from '../api/players';
import { isTopModal, popModal, pushModal } from './modalStack';
import PlayerAvatar from './PlayerAvatar';
import styles from './PlayerModal.module.css';

export interface PlayerSeed {
  id: number;
  name?: string | null;
  jersey?: string | null;
  position?: string | null;
}

interface PlayerModalProps {
  gamePk: number | undefined;
  playerId: number | null;
  /**
   * Optional pre-known identity passed from the click site (lineup row,
   * fielder badge, pitch-log chip). Lets the heading + jersey/pos line
   * paint synchronously on the first render — no skeleton flash when the
   * API/cache is still resolving.
   */
  seed?: PlayerSeed | null;
  onClose: () => void;
}

function pickHittingHeadline(stat: Record<string, unknown>): string {
  const parts: string[] = [];
  if (stat.avg) parts.push(`AVG ${stat.avg}`);
  if (stat.homeRuns != null) parts.push(`${stat.homeRuns} HR`);
  if (stat.runsBattedIn != null) parts.push(`${stat.runsBattedIn} RBI`);
  if (stat.ops) parts.push(`OPS ${stat.ops}`);
  return parts.join(' · ');
}

function pickPitchingHeadline(stat: Record<string, unknown>): string {
  const parts: string[] = [];
  if (stat.era) parts.push(`ERA ${stat.era}`);
  if (stat.wins != null && stat.losses != null) parts.push(`${stat.wins}-${stat.losses}`);
  if (stat.whip) parts.push(`WHIP ${stat.whip}`);
  if (stat.strikeOuts != null) parts.push(`${stat.strikeOuts} K`);
  return parts.join(' · ');
}

// Curated key→label maps for the current-game stat panels. Pick the stats
// a fan actually wants at a glance, not the full GUMBO dump.
const HITTING_KEYS: Array<{ key: string; label: string }> = [
  { key: 'atBats', label: 'AB' },
  { key: 'hits', label: 'H' },
  { key: 'runs', label: 'R' },
  { key: 'rbi', label: 'RBI' },
  { key: 'baseOnBalls', label: 'BB' },
  { key: 'strikeOuts', label: 'K' },
  { key: 'homeRuns', label: 'HR' },
  { key: 'leftOnBase', label: 'LOB' },
];
const PITCHING_KEYS: Array<{ key: string; label: string }> = [
  { key: 'inningsPitched', label: 'IP' },
  { key: 'hits', label: 'H' },
  { key: 'runs', label: 'R' },
  { key: 'earnedRuns', label: 'ER' },
  { key: 'baseOnBalls', label: 'BB' },
  { key: 'strikeOuts', label: 'K' },
  { key: 'pitchesThrown', label: 'P' },
  { key: 'strikes', label: 'S' },
];

export default function PlayerModal({ gamePk, playerId, seed = null, onClose }: PlayerModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  // Store the raw Element — could be HTMLElement (lineup name button, pitcher
  // chip) OR SVGElement (fielder/runner badge group). The narrowing matters at
  // close-time: see the cleanup function below.
  const opener = useRef<Element | null>(null);
  const stackToken = useRef<object>({});
  const { data, isLoading, isError, error } = usePlayerStats(gamePk, playerId);

  // Register on the modal stack — its keydown handler gates on topmost.
  useEffect(() => {
    if (playerId == null) return undefined;
    const token = stackToken.current;
    pushModal(token);
    return () => popModal(token);
  }, [playerId]);

  useEffect(() => {
    if (playerId == null) return undefined;
    opener.current = document.activeElement ?? null;
    closeBtnRef.current?.focus();
    return () => {
      // Skip focus-restore for SVG elements (fielder/runner badges). They use
      // <g role="button" tabIndex={0}> and Chrome/Safari treat the resulting
      // programmatic focus as :focus-visible, repainting the brightened-ring
      // styling after modal close. HTML openers (lineup name buttons, pitcher
      // chip, etc.) still get focus restored for keyboard accessibility.
      const o = opener.current;
      if (o instanceof HTMLElement) {
        o.focus();
      }
    };
  }, [playerId]);

  useEffect(() => {
    if (playerId == null) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (!isTopModal(stackToken.current)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll<HTMLElement>(
          'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [playerId, onClose]);

  if (playerId == null) return null;
  // Guard against opening into an invalid state (no gamePk) — query would
  // stay disabled and the modal would render the "Player {id}" fallback
  // shell. Just don't open instead.
  if (gamePk == null) return null;

  const seasonSplit = data?.season?.stats?.[0]?.splits?.[0]?.stat ?? null;
  const headline =
    seasonSplit && data?.season_group === 'pitching'
      ? pickPitchingHeadline(seasonSplit)
      : seasonSplit
        ? pickHittingHeadline(seasonSplit)
        : '';

  // Portal to <body> for parity with Modal.tsx. Rendering inline left the
  // dialog subject to any ancestor that creates a stacking context or a
  // containing block for `position: fixed`, and put it *earlier* in the
  // document than InningDetailModal's portal — so an equal z-index resolved
  // in the inning modal's favour and PlayerModal opened underneath it.
  return createPortal(
    <div
      data-testid="player-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={styles.backdrop}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="player-modal-heading"
        data-testid="player-modal"
        className={styles.dialog}
      >
        <header className={styles.header}>
          <PlayerAvatar
            // Identity stays with the request — never display a different
            // person's photo even if the API somehow returns a mismatched id.
            personId={playerId}
            name={seed?.name ?? data?.current_game?.name ?? null}
            size={240}
            displaySize={72}
            className={styles.headerAvatar}
          />
          <div className={styles.headerText}>
            {(() => {
              // Heading paints from seed on first render, then the API confirms.
              const displayName = seed?.name ?? data?.current_game?.name ?? null;
              if (displayName) {
                return (
                  <h2 id="player-modal-heading" className={styles.name}>
                    {displayName}
                  </h2>
                );
              }
              return (
                <h2
                  id="player-modal-heading"
                  className={`${styles.name} ${styles.nameSkeleton}`}
                  data-testid="player-modal-name-skeleton"
                  aria-label="Loading player name"
                >
                  <span className={styles.skeletonBar} />
                </h2>
              );
            })()}
            {(() => {
              const displayPos = seed?.position ?? data?.current_game?.position ?? null;
              const displayJersey = seed?.jersey ?? data?.current_game?.jersey ?? null;
              if (!displayPos && !displayJersey) return null;
              return (
                <div className={styles.posJersey} data-testid="player-modal-posjersey">
                  {displayPos}
                  {displayJersey ? ` · #${displayJersey}` : ''}
                </div>
              );
            })()}
          </div>
          <button
            type="button"
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close player stats"
            data-testid="player-modal-close"
            className={styles.close}
          >
            ×
          </button>
        </header>
        {isLoading && <p data-testid="player-modal-loading">Loading…</p>}
        {isError && (
          <p role="alert" data-testid="player-modal-error">
            Could not load player stats: {(error as Error)?.message ?? ''}
          </p>
        )}
        {data && (
          <>
            <section data-testid="player-modal-season" className={styles.section}>
              <h3 className={styles.sectionHeading}>
                Season {data.season_group === 'pitching' ? '(pitching)' : '(hitting)'}
              </h3>
              {data.season == null ? (
                <p data-testid="player-modal-season-missing" className={styles.missing}>
                  Season stats unavailable for this player.
                </p>
              ) : headline ? (
                <span className={styles.seasonBadge} data-testid="player-modal-season-badge">
                  {headline}
                </span>
              ) : (
                <p className={styles.headline}>See current-game line below.</p>
              )}
            </section>
            <section data-testid="player-modal-current" className={styles.section}>
              <h3 className={styles.sectionHeading}>This game</h3>
              {(() => {
                const isPitcher = data.current_game.pitching != null;
                const source = (data.current_game.pitching ??
                  data.current_game.batting ??
                  null) as Record<string, unknown> | null;
                if (!source) {
                  return <p className={styles.missing}>No in-game line yet.</p>;
                }
                const keys = isPitcher ? PITCHING_KEYS : HITTING_KEYS;
                const summary = typeof source.summary === 'string' ? source.summary : null;
                return (
                  <>
                    {summary && <p className={styles.summary}>{summary}</p>}
                    <div className={styles.statsGrid}>
                      {keys
                        .filter((k) => source[k.key] != null)
                        .map((k) => (
                          <div key={k.key} className={styles.statCell}>
                            <span className={styles.statLabel}>{k.label}</span>
                            <span className={styles.statValue}>{String(source[k.key])}</span>
                          </div>
                        ))}
                    </div>
                  </>
                );
              })()}
            </section>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
